import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faCircleCheck, faTriangleExclamation, faLightbulb,
  faArrowsRotate, faClock, faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "./confirmationModal.js";
import KnowledgeLimitInfoModal from "./knowledgeLimitInfoModal.js";
import LinkBucketSection from "./LinkBucketSection.js";
import useLinkDiscovery from "../hooks/useLinkDiscovery.js";
import "../translations.js";
import "./CheckForUpdatesModal.css";

/**
 * One-click "Check for updates".
 *
 * Two steps and one decision: the dialog scans the customer's site on open
 * (no URL to type, the backend resolves it from the bot), then shows what would
 * change as three group-level opt-ins. A single confirm applies everything.
 *
 * Removal starts unticked on purpose: it deletes the row AND its vectors with no
 * undo. The backend additionally withholds removal candidates whose crawl looked
 * unhealthy, and reports that as removal_blocked.
 */
const CheckForUpdatesModal = ({
  open,
  onClose,
  onApply,
  botId,
  existingLinks = [],
  remainingSlots = Infinity,
  onUpgrade,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const startedRef = useRef(false);

  const discovery = useLinkDiscovery({
    botId,
    existingLinks,
    selectMissingByDefault: false,
    autoExpandFirst: false,
  });
  const {
    isFetching, errorMsg, rateLimited, hasResult, removalBlocked, bases,
    newLinks, existing, missing,
    selectedNew, selectedExisting, selectedMissing,
    setSelectedNew, setSelectedExisting, setSelectedMissing,
    expandedPanel, toggleExpand, reset, startUpdateCheck,
  } = discovery;

  // Scan as soon as the dialog opens; the whole point is that one click is enough.
  useEffect(() => {
    if (open && !startedRef.current) {
      startedRef.current = true;
      startUpdateCheck();
    }
    if (!open) {
      startedRef.current = false;
      setConfirmRemoveOpen(false);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalSelected = selectedNew.length + selectedExisting.length + selectedMissing.length;
  const nothingToDo = hasResult && newLinks.length === 0 && existing.length === 0 && missing.length === 0;

  const scanTarget = useMemo(() => {
    if (!bases || bases.length === 0) return "";
    try {
      const host = new URL(bases[0]).host;
      return bases.length > 1 ? `${host} +${bases.length - 1}` : host;
    } catch {
      return bases[0];
    }
  }, [bases]);

  const groupToggle = (setter, all) => (e) => {
    setter(e.target.checked ? all : []);
  };

  const applyNow = () => {
    onApply({
      addUrls: Array.from(new Set(selectedNew)),
      rescrapeUrls: Array.from(new Set(selectedExisting)),
      removeUrls: Array.from(new Set(selectedMissing)),
    });
    onClose();
  };

  const handleConfirm = () => {
    if (totalSelected === 0) return;
    if (selectedNew.length > remainingSlots) {
      setLimitModalOpen(true);
      return;
    }
    // Deleting pages is the one irreversible part, so it keeps its own gate even
    // though the customer already ticked the group.
    if (selectedMissing.length > 0) {
      setConfirmRemoveOpen(true);
      return;
    }
    applyNow();
  };

  const renderBody = () => {
    if (isFetching) {
      return (
        <div className="cfu-state">
          <FontAwesomeIcon icon={faSpinner} spin className="cfu-state-icon" />
          <h3 className="cfu-state-title">
            {scanTarget ? t("update_check_scanning_site", { site: scanTarget }) : t("update_check_scanning")}
          </h3>
          <p className="cfu-state-text">{t("update_check_scanning_hint")}</p>
        </div>
      );
    }

    if (rateLimited) {
      return (
        <div className="cfu-state">
          <FontAwesomeIcon icon={faClock} className="cfu-state-icon cfu-state-icon-muted" />
          <h3 className="cfu-state-title">{t("update_check_rate_limited_title")}</h3>
          <p className="cfu-state-text">{t("update_check_rate_limited_text")}</p>
        </div>
      );
    }

    if (errorMsg && !hasResult) {
      return (
        <div className="cfu-state">
          <FontAwesomeIcon icon={faTriangleExclamation} className="cfu-state-icon cfu-state-icon-warning" />
          <h3 className="cfu-state-title">{t("update_check_failed_title")}</h3>
          <p className="cfu-state-text">{errorMsg}</p>
        </div>
      );
    }

    if (nothingToDo) {
      return (
        <div className="cfu-state">
          <FontAwesomeIcon icon={faCircleCheck} className="cfu-state-icon cfu-state-icon-success" />
          <h3 className="cfu-state-title">{t("update_check_up_to_date_title")}</h3>
          <p className="cfu-state-text">{t("update_check_up_to_date_text")}</p>
        </div>
      );
    }

    return (
      <>
        <p className="cfu-summary-lead">
          {scanTarget ? t("update_check_result_lead_site", { site: scanTarget }) : t("update_check_result_lead")}
        </p>

        <div className="fnl-results cfu-results">
          <LinkBucketSection
            icon={faLightbulb}
            title={t("update_check_group_add")}
            links={newLinks}
            selected={selectedNew}
            expanded={expandedPanel === "new"}
            onToggleExpand={(hasData) => toggleExpand("new", hasData)}
            onToggleLink={discovery.toggleNewLink}
            onSelectAll={discovery.selectAllNew}
            showSelectedCount={false}
            headerCheckbox={{
              checked: selectedNew.length > 0,
              indeterminate: selectedNew.length > 0 && selectedNew.length < newLinks.length,
              onChange: groupToggle(setSelectedNew, newLinks),
            }}
          />

          <LinkBucketSection
            icon={faCircleCheck}
            title={t("update_check_group_refresh")}
            links={existing}
            selected={selectedExisting}
            expanded={expandedPanel === "existing"}
            onToggleExpand={(hasData) => toggleExpand("existing", hasData)}
            onToggleLink={discovery.toggleExistingLink}
            onSelectAll={discovery.selectAllExisting}
            showSelectedCount={false}
            headerCheckbox={{
              checked: selectedExisting.length > 0,
              indeterminate: selectedExisting.length > 0 && selectedExisting.length < existing.length,
              onChange: groupToggle(setSelectedExisting, existing),
            }}
          />

          <LinkBucketSection
            icon={faTriangleExclamation}
            iconClassName="links-status-icon-warning"
            title={t("update_check_group_remove")}
            links={missing}
            selected={selectedMissing}
            expanded={expandedPanel === "missing"}
            onToggleExpand={(hasData) => toggleExpand("missing", hasData)}
            onToggleLink={discovery.toggleMissingLink}
            onSelectAll={discovery.selectAllMissing}
            showSelectedCount={false}
            headerCheckbox={{
              checked: selectedMissing.length > 0,
              indeterminate: selectedMissing.length > 0 && selectedMissing.length < missing.length,
              onChange: groupToggle(setSelectedMissing, missing),
            }}
          />
        </div>

        {removalBlocked && (
          <p className="cfu-note">{t("update_check_removal_blocked_note")}</p>
        )}
        <p className="cfu-note cfu-note-muted">{t("update_check_unchanged_note")}</p>
      </>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: isMobile ? "90%" : "80%",
            maxWidth: 750,
            maxHeight: "88%",
            overflow: "auto",
            outline: "none",
          }}
        >
          <div className="cfu-header">
            <h2 className="fnl-title cfu-title">
              <FontAwesomeIcon icon={faArrowsRotate} className="cfu-title-icon" />
              {t("check_for_updates")}
            </h2>
            <button type="button" className="cfu-close" onClick={onClose} aria-label={t("cancel")}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {renderBody()}

          <Box display="flex" justifyContent="flex-end" gap={2} className="fnl-actions">
            <Button
              onClick={onClose}
              variant="outlined"
              className="fnl-cancel-btn"
              sx={{
                color: "#5616ea",
                borderColor: "#5616ea",
                "&:hover": { borderColor: "#5e0fb3", backgroundColor: "#f3eaff" },
              }}
            >
              {hasResult && !nothingToDo ? t("cancel") : t("close")}
            </Button>
            {hasResult && !nothingToDo && (
              <Button
                onClick={handleConfirm}
                variant="contained"
                className="fnl-primary-btn"
                disabled={totalSelected === 0}
                sx={{
                  backgroundColor: "#5616ea",
                  "&:hover": { backgroundColor: "#4a14d6" },
                  "&.Mui-disabled": { backgroundColor: "#cfc7ff" },
                }}
              >
                {t("update_knowledge_count", { count: totalSelected })}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      <ConfirmationModal
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        onConfirm={() => { setConfirmRemoveOpen(false); applyNow(); }}
        itemName={`${selectedMissing.length} ${t("selected")}`}
        requireTextConfirmation={false}
      />

      <KnowledgeLimitInfoModal
        open={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onUpgrade={() => { setLimitModalOpen(false); onUpgrade && onUpgrade(); }}
        remainingSlots={remainingSlots}
      />
    </>
  );
};

export default CheckForUpdatesModal;
